import {
    type Dispatch,
    type RefObject,
    type SetStateAction,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    type CreateOrderData,
    type OnApproveActions,
    type OnApproveData,
    type PayPalButtonsComponent,
    type PayPalButtonsComponentOptions,
} from '@paypal/paypal-js/types/components/buttons';
import { type AxiosError } from 'axios';

import { mapPayPalOrderToPersonalInformation } from '@jsmdg/cart-library-ts';
import { PackagingType } from '@jsmdg/cart-library-ts/shared';
import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    CheckoutCategorySubtype,
    CheckoutStep,
    type ClickType,
    EventLabelCartButtonClickEnum,
    GA4FeatureCategory,
    trackCartExpressPaymentClick,
} from '@jsmdg/tracking';
import { PaymentMethod } from '../../shared/paymentTypes';
import { isExperience, isValueVoucherWithPriceRange } from '../../shared/product-safeguards';
import { type Location, type Price, type Product } from '../../shared/types';
import {
    trackBuyNowEecCheckout,
    trackEECAddToCartAdded,
} from '../components/BuyboxContainer/buyboxContainer.tracking';
import { CartError } from '../errors/cartError';
import { ErrorTypes } from '../errors/errorTypes';
import { getPurchaseType } from '../helper/getPurchaseType';
import { saveCheckoutInformationInSession } from '../services/saveCheckoutInformationInSession';
import { eventTypes } from '../types';
import { type PurchaseType } from '../types/enums/purchaseType';
import { type ProductDetailContext } from '../types/types';
import { useExpressCheckoutErrors } from './useExpressCheckoutErrors';
import { usePrice } from './usePrice';
import { useProduct } from './useProduct';

interface UsePayPalExpressBuyboxReturn {
    setPayPalButtonWrapperElement: Dispatch<SetStateAction<HTMLDivElement | undefined>>;
    isLoading: boolean;
}

type UsePayPalExpressBuyboxParams = {
    productId: string;
    quantity: number;
    buttonHeight: number;
    clickType: ClickType;
    onAddToCartError: (errorCode?: number) => void;
    handleError: (error: CartError) => void;
};

type PayPalCheckoutSession = {
    paypalOrderId: string;
    orderId?: string;
    paymentTransactionId?: string;
};

type PayPalButtonsConfig = Required<
    Pick<PayPalButtonsComponentOptions, 'onClick' | 'createOrder' | 'onApprove' | 'style'>
>;

type CreatePayPalExpressOrderArgs = {
    product: Product;
    price: Price;
    productId: string;
    quantity: number;
    locations: Location[];
    hasFriendsDiscount: boolean;
    purchaseType?: PurchaseType;
    addItemToCart: ProductDetailContext['addItemToCart'];
    createAddToCartButtonClickedEvent: ProductDetailContext['createAddToCartButtonClickedEvent'];
    updateItemPackaging: ProductDetailContext['updateItemPackaging'];
    createPaymentSession: ProductDetailContext['createPaymentSession'];
    checkoutSessionRef: RefObject<PayPalCheckoutSession>;
    onAddToCartError: (errorCode?: number) => void;
    handleError: (error: CartError) => void;
    onCreateOrder: ReturnType<typeof useExpressCheckoutErrors>['onCreateOrder'];
};

type ApprovePayPalExpressOrderArgs = {
    actions: OnApproveActions;
    checkoutSessionRef: RefObject<PayPalCheckoutSession>;
    handleAddressErrors: ReturnType<typeof useExpressCheckoutErrors>['handleAddressErrors'];
    handleError: (error: CartError) => void;
    lastCheckUrlTemplate: string;
    setIsLoading: (isLoading: boolean) => void;
    validatePersonalInformation: ProductDetailContext['validatePersonalInformation'];
};

async function createPayPalExpressOrder({
    addItemToCart,
    checkoutSessionRef,
    createAddToCartButtonClickedEvent,
    createPaymentSession,
    handleError,
    hasFriendsDiscount,
    locations,
    onAddToCartError,
    onCreateOrder,
    price,
    product,
    productId,
    purchaseType,
    quantity,
    updateItemPackaging,
}: CreatePayPalExpressOrderArgs): Promise<string> {
    try {
        if (!navigator.cookieEnabled) {
            throw new CartError(ErrorTypes.GENERIC_PAYPAL_EXPRESS_ERROR, null, {
                errorOrigin: '[PayPalExpressBuybox] createOrder',
                reason: 'cookies disabled',
            });
        }

        const valueVoucherPrice = isValueVoucherWithPriceRange(product) ? price.gross : undefined;

        const cartResult = await addItemToCart({
            productId,
            quantity,
            valueVoucherPrice,
        });

        await createAddToCartButtonClickedEvent({
            productId,
            cartId: cartResult.cart.cartId,
        });

        const addedItem = cartResult.cart.items.find(
            item => item.itemId === cartResult.currentItemId,
        );
        const hasPdfOption = addedItem?.availablePackagingOptions?.some(
            opt => opt.type === PackagingType.PDF,
        );

        let trackingCart = cartResult.cart;

        if (hasPdfOption && cartResult.currentItemId) {
            trackingCart = await updateItemPackaging({
                itemId: cartResult.currentItemId,
                packagingType: PackagingType.PDF,
            });
        }

        await window.yieldToMainThread();
        trackEECAddToCartAdded(
            product,
            price,
            locations,
            quantity,
            hasFriendsDiscount,
            purchaseType,
            CheckoutCategorySubtype.CART_BUY_NOW,
        );
        trackBuyNowEecCheckout(
            trackingCart,
            CheckoutStep.CART,
            CheckoutCategorySubtype.CART_BUY_NOW,
        );

        const {
            orderId,
            paypalOrderId: createdPaypalOrderId,
            paymentTransactionId,
        } = await createPaymentSession(PaymentMethod.PayPal);

        checkoutSessionRef.current = {
            orderId,
            paypalOrderId: createdPaypalOrderId ?? '',
            paymentTransactionId,
        };

        return checkoutSessionRef.current.paypalOrderId;
    } catch (error: unknown) {
        const axiosError = error as AxiosError<{ errorCode?: number }>;
        const errorCode = axiosError.response?.data?.errorCode;

        if (errorCode !== undefined) {
            onAddToCartError(errorCode);
        }

        const createPaypalOrderException = onCreateOrder(error, {
            message: 'PayPalExpressCreateOrderException',
            origin: '[PayPalExpressBuybox] createOrder',
            defaultErrorType: ErrorTypes.GENERIC_PAYPAL_EXPRESS_ERROR,
            withDefaultError: !errorCode,
        });

        if (!errorCode) {
            handleError(createPaypalOrderException);
        }

        throw createPaypalOrderException;
    }
}

async function approvePayPalExpressOrder({
    actions,
    checkoutSessionRef,
    handleAddressErrors,
    handleError,
    lastCheckUrlTemplate,
    setIsLoading,
    validatePersonalInformation,
}: ApprovePayPalExpressOrderArgs): Promise<void> {
    setIsLoading(true);
    try {
        const paypalOrder = await actions.order?.get();
        const personalInformation = mapPayPalOrderToPersonalInformation(paypalOrder);
        const { paypalOrderId, orderId, paymentTransactionId } = checkoutSessionRef.current;

        await validatePersonalInformation(personalInformation);
        saveCheckoutInformationInSession({
            personalInformation,
            paypalOrderId,
            evelinOrderId: orderId ?? '',
            paymentTransactionId,
        });

        window.location.href = `${window.location.origin}${lastCheckUrlTemplate}`;
    } catch (originalError: unknown) {
        const error = handleAddressErrors(originalError, {
            origin: '[PayPalExpressBuybox] onApprove',
        });

        setIsLoading(false);
        handleError(error);
    }
}

function usePayPalExpressBuybox({
    productId,
    quantity,
    buttonHeight,
    clickType,
    onAddToCartError,
    handleError,
}: UsePayPalExpressBuyboxParams): UsePayPalExpressBuyboxReturn {
    const product = useProduct();
    const { price } = usePrice();
    const { hasFriendsDiscount } = product;
    const locations = isExperience(product) ? product.locations : [];
    const sellOption = isExperience(product) ? product.sellOption : undefined;

    const {
        paymentServiceProviderConfig: { paypalClientScriptUrl },
        lastCheckUrlTemplate,
        addItemToCart,
        updateItemPackaging,
        createPaymentSession,
        validatePersonalInformation,
        createAddToCartButtonClickedEvent,
    } = useFragmentContext<ProductDetailContext>();

    const [purchaseType, setPurchaseType] = useState<PurchaseType | undefined>(() =>
        getPurchaseType(sellOption),
    );
    const [payPalButtonWrapperElement, setPayPalButtonWrapperElement] = useState<
        HTMLDivElement | undefined
    >(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [isPayPalScriptReady, setIsPayPalScriptReady] = useState(() => Boolean(window.paypal));

    const checkoutSessionRef = useRef<PayPalCheckoutSession>({ paypalOrderId: '' });
    const { handleAddressErrors, onCreateOrder } = useExpressCheckoutErrors();

    useEffect(() => {
        const handleBuyboxEvent = (event: Event) => {
            const nextPurchaseType = (event as CustomEvent<{ purchaseType?: PurchaseType }>).detail
                ?.purchaseType;

            if (nextPurchaseType) {
                setPurchaseType(nextPurchaseType);
            }
        };

        document.addEventListener(eventTypes.BUYBOX_SELECTION_CHANGE, handleBuyboxEvent);

        return () => {
            document.removeEventListener(eventTypes.BUYBOX_SELECTION_CHANGE, handleBuyboxEvent);
        };
    }, []);

    const handleOnClick = async (): Promise<void> => {
        await window.yieldToMainThread();
        trackCartExpressPaymentClick(
            EventLabelCartButtonClickEnum.PaypalExpress,
            'paypal express button',
            'Paypal',
            clickType,
            GA4FeatureCategory.ProductdetailBuybox,
        );
    };

    const handleCreateOrder = (_: CreateOrderData): Promise<string> =>
        createPayPalExpressOrder({
            addItemToCart,
            checkoutSessionRef,
            createAddToCartButtonClickedEvent,
            createPaymentSession,
            handleError,
            hasFriendsDiscount,
            locations,
            onAddToCartError,
            onCreateOrder,
            price,
            product,
            productId,
            purchaseType,
            quantity,
            updateItemPackaging,
        });

    const handleOnApprove = (_data: OnApproveData, actions: OnApproveActions): Promise<void> =>
        approvePayPalExpressOrder({
            actions,
            checkoutSessionRef,
            handleAddressErrors,
            handleError,
            lastCheckUrlTemplate,
            setIsLoading,
            validatePersonalInformation,
        });

    const buttonsOptions: PayPalButtonsConfig = {
        onClick: handleOnClick,
        createOrder: handleCreateOrder,
        onApprove: handleOnApprove,
        style: {
            disableMaxWidth: false,
            layout: 'horizontal',
            tagline: false,
            label: 'checkout',
            height: buttonHeight,
        },
    };

    const buttonsOptionsRef = useRef(buttonsOptions);

    useEffect(() => {
        buttonsOptionsRef.current = buttonsOptions;
    }, [buttonsOptions]);

    useEffect(() => {
        if (!paypalClientScriptUrl) {
            return undefined;
        }

        if (window.paypal) {
            setIsPayPalScriptReady(true);
            return undefined;
        }

        const handleScriptLoad = (): void => {
            setIsPayPalScriptReady(true);
        };

        const existingScript = document.querySelector<HTMLScriptElement>(
            'script[data-testid="paypal-script"]',
        );

        if (existingScript) {
            if (window.paypal) {
                setIsPayPalScriptReady(true);
            } else {
                existingScript.addEventListener('load', handleScriptLoad);
            }

            return () => {
                existingScript.removeEventListener('load', handleScriptLoad);
            };
        }

        const payPalScriptElement = document.createElement('script');
        payPalScriptElement.src = paypalClientScriptUrl;
        payPalScriptElement.dataset.testid = 'paypal-script';
        payPalScriptElement.async = true;
        payPalScriptElement.addEventListener('load', handleScriptLoad);
        document.body.appendChild(payPalScriptElement);

        return () => {
            payPalScriptElement.removeEventListener('load', handleScriptLoad);
        };
    }, [paypalClientScriptUrl]);

    useEffect(() => {
        if (!payPalButtonWrapperElement || !isPayPalScriptReady || !window.paypal?.Buttons) {
            return undefined;
        }

        const wrapper = payPalButtonWrapperElement;
        wrapper.replaceChildren();

        const paypalButtons: PayPalButtonsComponent = window.paypal.Buttons({
            onClick: (data, actions) => buttonsOptionsRef.current.onClick(data, actions),
            createOrder: (data, actions) => buttonsOptionsRef.current.createOrder(data, actions),
            onApprove: (data, actions) => buttonsOptionsRef.current.onApprove(data, actions),
            style: buttonsOptionsRef.current.style,
        });

        void paypalButtons.render(wrapper);

        return () => {
            paypalButtons.close?.();
        };
    }, [buttonHeight, isPayPalScriptReady, payPalButtonWrapperElement]);

    return { setPayPalButtonWrapperElement, isLoading };
}

export { usePayPalExpressBuybox };
